import { useEffect, useState } from "react";
import { FileUp, Megaphone, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export default function StaffPortalTab() {
  const [overview, setOverview] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [circulars, setCirculars] = useState([]);
  const [cTitle, setCTitle] = useState("");
  const [cBody, setCBody] = useState("");
  const [cTargets, setCTargets] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [cMode, setCMode] = useState("text");
  const [cFile, setCFile] = useState(null);

  const loadAll = () => {
    api.get("/portal/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/portal/employees").then((r) =>
      // الموظفون المستثنون لا تظهر أسماؤهم في قوائم التعاميم
      setEmployees((r.data || []).filter((e) => !e.excluded))).catch(() => {});
    api.get("/portal/circulars").then((r) => setCirculars(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const sendCircular = async () => {
    if (!cTitle.trim()) { toast.error("أدخل عنوان التعميم"); return; }
    if (cMode === "text" && !cBody.trim()) { toast.error("أدخل نص التعميم"); return; }
    if (cMode === "pdf" && !cFile) { toast.error("اختر ملف PDF للتعميم"); return; }
    setSending(true);
    try {
      let r;
      if (cMode === "pdf") {
        const fd = new FormData();
        fd.append("title", cTitle.trim());
        fd.append("employee_ids", JSON.stringify([...cTargets]));
        fd.append("file", cFile);
        r = await api.post("/portal/circulars/pdf", fd);
      } else {
        r = await api.post("/portal/circulars",
          { title: cTitle.trim(), body: cBody.trim(), employee_ids: [...cTargets] });
      }
      toast.success(`صدر التعميم إلى ${r.data.sent} موظف`);
      setCTitle(""); setCBody(""); setCTargets(new Set()); setCFile(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "فشل الإرسال"); }
    finally { setSending(false); }
  };

  const viewCircularPdf = (c) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("المتصفح منع النافذة المنبثقة — اسمح بالنوافذ المنبثقة ثم أعد المحاولة"); return; }
    api.get(`/portal/circulars/${c.id}/pdf`, { responseType: "blob" })
      .then((r) => { w.location = URL.createObjectURL(r.data); })
      .catch(() => { w.close(); toast.error("تعذر فتح الملف"); });
  };

  const viewCircularSig = async (c, s) => {
    try {
      const r = await api.get(`/portal/circulars/${c.id}/signatures/${s.employee_doc_id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("تعذر فتح التوقيع"); }
  };

  const pickToggle = (setter, id) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const EmployeePicker = ({ selected, setter, testId }) => (
    <div className="border rounded-xl max-h-40 overflow-y-auto divide-y" data-testid={testId}>
      {employees.map((e) => (
        <label key={e.id} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
          <Checkbox checked={selected.has(e.id)} onCheckedChange={() => pickToggle(setter, e.id)}
            data-testid={`${testId}-check-${e.id}`} />
          <span className="truncate">{e.name}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="staff-portal-tab">
      <div className="bg-white rounded-2xl border shadow-sm p-4 space-y-3" data-testid="portal-send-circular-card">
          <p className="font-bold text-sm text-emerald-950 flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-emerald-800" /> إصدار تعميم جديد
          </p>
          <Input value={cTitle} onChange={(e) => setCTitle(e.target.value)} placeholder="عنوان التعميم"
            className="h-9 text-sm" data-testid="portal-circular-title" />
          <div className="grid grid-cols-2 gap-2" data-testid="circ-mode-toggle">
            <button type="button" onClick={() => setCMode("text")} data-testid="circ-mode-text"
              className={`h-9 rounded-xl border-2 text-xs font-bold transition-colors ${cMode === "text" ? "border-emerald-800 bg-emerald-50 text-emerald-950" : "border-slate-200 text-slate-500"}`}>
              نص مكتوب
            </button>
            <button type="button" onClick={() => setCMode("pdf")} data-testid="circ-mode-pdf"
              className={`h-9 rounded-xl border-2 text-xs font-bold transition-colors ${cMode === "pdf" ? "border-emerald-800 bg-emerald-50 text-emerald-950" : "border-slate-200 text-slate-500"}`}>
              ملف PDF
            </button>
          </div>
          {cMode === "text" ? (
            <textarea value={cBody} onChange={(e) => setCBody(e.target.value)} rows={3}
              placeholder="نص التعميم..."
              data-testid="portal-circular-body"
              className="w-full rounded-xl border border-slate-200 p-3 text-sm leading-6 focus:outline-none focus:border-emerald-600" />
          ) : (
            <label data-testid="circ-pdf-drop"
              className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-4 text-xs font-bold text-slate-500 cursor-pointer hover:border-emerald-600 hover:text-emerald-800 transition-colors">
              <FileUp className="w-4 h-4" />
              <span data-testid="circ-pdf-name">{cFile ? cFile.name : "اختر ملف التعميم (PDF — حتى 5MB)"}</span>
              <input type="file" accept="application/pdf,.pdf" className="hidden"
                data-testid="circ-pdf-file"
                onChange={(e) => setCFile(e.target.files?.[0] || null)} />
            </label>
          )}
          <EmployeePicker selected={cTargets} setter={setCTargets} testId="portal-cir-targets" />
          <p className="text-[10px] text-slate-400">بدون تحديد = يُرسل لجميع الموظفين</p>
          <Button onClick={sendCircular} disabled={sending} data-testid="portal-send-circular"
            className="w-full h-10 bg-emerald-900 hover:bg-emerald-950 text-white gap-1.5">
            <Send className="w-4 h-4" /> إصدار التعميم{cTargets.size ? ` (${cTargets.size})` : " (للجميع)"}
          </Button>
      </div>

      <div className="bg-white rounded-2xl border shadow-sm p-4" data-testid="portal-circulars-card">
        <p className="font-bold text-sm text-emerald-950 mb-3">التعاميم وحالة التوقيع</p>
        <div className="space-y-2">
          {circulars.length === 0 && <p className="text-xs text-slate-400">لا توجد تعاميم</p>}
          {circulars.map((c) => (
            <div key={c.id} className="border rounded-xl px-3 py-2.5 space-y-2" data-testid={`portal-circular-${c.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-bold flex-1">{c.title}</p>
                <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                  data-testid={`portal-circular-signed-count-${c.id}`}>
                  وقّع {c.signed.length} / {c.total_targets}
                </span>
                {c.has_pdf && (
                  <button onClick={() => viewCircularPdf(c)} data-testid={`portal-circular-pdf-${c.id}`}
                    className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 hover:bg-blue-100">
                    عرض PDF
                  </button>
                )}
                <span className="text-[10px] text-slate-400" dir="ltr">{c.date}</span>
              </div>
              {c.signed.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {c.signed.map((s) => (
                    <button key={s.employee_doc_id} onClick={() => viewCircularSig(c, s)}
                      data-testid={`portal-cir-sig-${c.id}-${s.employee_doc_id}`}
                      className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 hover:bg-emerald-100">
                      ✓ {s.name}
                    </button>
                  ))}
                </div>
              )}
              {c.unsigned_names.length > 0 && (
                <p className="text-[10px] text-slate-400">لم يوقع بعد: {c.unsigned_names.join("، ")}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
